'use client'

import { Button as DSButton } from '@lastbrand/web-ui/button'

import {
  ProductComparisonCarouselDS,
  comparisonCtaCss,
  type ProductComparisonCarouselDSItem,
} from '@src/components/blocks/product-comparison-carousel-ds'
import type { PLTypedWidgetProps } from '@src/components/pl-base-widget'

import { WidgetMedia, WidgetRichText, readButton } from '../widget-content'
import { getSafeHref } from '../widget-utils'

import type { ProductComparisonCarouselWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'
import type { AnchorHTMLAttributes } from 'react'

/**
 * PRODUCT_COMPARISON_CAROUSEL widget — DS (Panda) path.
 *
 * Thin adapter: normalizes the proto payload through the same `widget-content` helpers as the
 * legacy path (identical data plumbing — media URL fixes, rich-text normalization, button URL
 * allowlisting), then renders the shared `ProductComparisonCarouselDS` block. The CTA is the DS
 * Button (tertiary/lg — the Figma pill); like legacy, it carries no click telemetry. An unsafe
 * button URL degrades to a disabled non-navigating button (legacy rendered a bare `<span>`).
 */

const PLAIN_LINK = ({ children, ...linkProps }: AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a {...linkProps}>{children}</a>
)

function CtaButtons({ buttons }: Readonly<{ buttons: readonly Record<string, unknown>[] }>) {
  const parsed = buttons.map(readButton).filter((b): b is { label: string; url?: string } => b !== null)
  if (parsed.length === 0) return null

  return (
    <>
      {parsed.map((button, index) => {
        const key = `${button.label}-${index}`
        const href = getSafeHref(button.url)
        return href ? (
          <DSButton key={key} variant="tertiary" size="lg" css={comparisonCtaCss} LinkComponent={PLAIN_LINK} href={href}>
            {button.label}
          </DSButton>
        ) : (
          <DSButton key={key} variant="tertiary" size="lg" css={comparisonCtaCss} disabled>
            {button.label}
          </DSButton>
        )
      })}
    </>
  )
}

export function ProductComparisonCarouselDSWidget(props: PLTypedWidgetProps<ProductComparisonCarouselWidgetData>) {
  const data = props.data.productComparisonCarouselData

  // Presence checks keep the block from rendering empty styled cells (the block's wrappers carry
  // padding/borders, so an always-truthy React element would leave hairlines behind missing data).
  const items: ProductComparisonCarouselDSItem[] = (data?.items ?? []).map((item, index) => ({
    // `name` is rich text (no plain title), so key off its Contentful id when present;
    // index fallback covers items the CMS didn't stamp. These lists aren't reordered.
    key: String((item.name as { contentful_id?: string } | undefined)?.contentful_id ?? index),
    media: item.media ? <WidgetMedia media={item.media} /> : undefined,
    name: item.name ? <WidgetRichText data={item.name} /> : undefined,
    specs: item.specs.map((spec, specIndex) => ({
      key: String(specIndex),
      label: spec.label ? <WidgetRichText data={spec.label} /> : undefined,
      value: spec.value ? <WidgetRichText data={spec.value} /> : undefined,
    })),
    cta: item.buttons.length ? <CtaButtons buttons={item.buttons} /> : undefined,
  }))

  return (
    <ProductComparisonCarouselDS
      heading={data?.heading ? <WidgetRichText data={data.heading} /> : undefined}
      subHeading={data?.subHeading ? <WidgetRichText data={data.subHeading} /> : undefined}
      items={items}
      // eslint-disable-next-line i18next/no-literal-string -- test id, not user copy
      testId="product-comparison-carousel-widget-ds"
    />
  )
}
